import { useGraphCacheStore } from '@tunasong/graph-cache'
import { features, useSelector, useStore, useThunkDispatch } from '@tunasong/redux'
import type { EntityConflict } from '@tunasong/redux'
import type { Entity } from '@tunasong/schemas'
import { useEffect } from 'react'
import { pinHeldEdit } from '../impl/optimistic-claims.js'

/** The part of a held conflict a panel shows: the held fields among `fields`, with both versions of those only */
const scoped = (conflict: EntityConflict | undefined, fields?: readonly string[]): EntityConflict | undefined => {
  if (!conflict || !fields) {
    return conflict
  }
  const shown = conflict.fields.filter(field => fields.includes(field))
  if (shown.length === 0) {
    return undefined
  }
  const pick = <T extends object>(source: T) =>
    Object.fromEntries(Object.entries(source).filter(([key]) => shown.includes(key))) as T
  return { fields: shown, mine: pick(conflict.mine), theirs: pick(conflict.theirs) }
}

/**
 * A held conflict on one entity and the three ways out of it (GH #1912 option B,
 * docs/ux/2026-10-24-conflict-notice-ux.md, Behaviour). The notice UI renders `conflict` and calls these.
 *
 * `fields` scopes it to one panel: the conflict lists only those fields, and Keep and Use their version resolve only
 * those. Another panel's held field on the same entity stays held, and its edit stays showing (spec, Edge cases).
 */
export const useEntityConflict = (entityId?: string, fields?: readonly string[]) => {
  const held = useSelector(state => (entityId ? state.entities.conflicts[entityId] : undefined))
  const conflict = scoped(held, fields)
  const dispatch = useThunkDispatch()
  const store = useStore()

  /** My edit of the fields still held after a resolution stays pinned over the server's version */
  const repinRemaining = (id: string) => pinHeldEdit(id, store.getState().entities.conflicts[id]?.mine)

  /** "Keep my changes": save my latest held edit over theirs. Rejects on failure, keeping the conflict */
  const keepMine = async () => {
    if (!entityId) {
      return
    }
    await dispatch(features.entities.conflicts.keepHeldEdit({ id: entityId, fields })).unwrap()
    repinRemaining(entityId)
  }

  /** "Use their version": drop my held edit and show theirs. Returns my edit, for Undo */
  const takeTheirs = () => {
    if (!entityId) {
      return undefined
    }
    const resolved = dispatch(features.entities.conflicts.takeTheirVersion({ id: entityId, fields }))
    repinRemaining(entityId)
    if (resolved) {
      useGraphCacheStore.getState().partialUpdateCache(entityId, resolved.theirs)
      /** Editors holding the typed value remount on this, and show theirs */
      dispatch(features.entities.actions.bumpConflictResolution({ id: entityId }))
    }
    return resolved?.mine
  }

  /** Undo after "Use their version": show my edit again and save it, as Keep does */
  const undoTakeTheirs = async (mine: Partial<Entity>) => {
    if (!entityId) {
      return
    }
    const cache = useGraphCacheStore.getState()
    cache.optimisticUpdateCache(entityId, mine)
    const seq = cache.lastOptimisticSeq(entityId)
    /** And remount again, showing mine */
    dispatch(features.entities.actions.bumpConflictResolution({ id: entityId }))
    try {
      await dispatch(features.entities.conflicts.undoTakeTheirVersion({ id: entityId, mine })).unwrap()
    } finally {
      if (seq !== undefined) {
        useGraphCacheStore.getState().confirmOptimistic(entityId, [seq])
      }
    }
  }

  return { conflict, keepMine, takeTheirs, undoTakeTheirs }
}

/**
 * A key for an editor that keeps its value in uncontrolled inputs or a local draft (GH #1912): it changes when "Use
 * their version" or its Undo replaced the value, so keying on it remounts the editor with the store's value.
 */
export const useConflictResolutionKey = (entityId?: string) =>
  useSelector(state => (entityId ? (state.entities.conflictResolutions[entityId] ?? 0) : 0))

/**
 * Save every held edit when the page is hidden or closed, unconditionally, so mine wins as it did before the notice
 * (spec, Behaviour). Mount once, app-wide. `pagehide` fires on close, navigation away and bfcache, where `unload`
 * does not reliably.
 */
export const useConflictKeepalive = () => {
  const dispatch = useThunkDispatch()
  const hasConflicts = useSelector(state => Object.keys(state.entities.conflicts).length > 0)
  useEffect(() => {
    if (!hasConflicts) {
      return
    }
    const handlePageHide = () => {
      void dispatch(features.entities.conflicts.resendHeldConflicts())
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [dispatch, hasConflicts])
}
